import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/StayFur.png";
import profileImg from "../assets/avatar-placeholder.png";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import axiosInstance from "../lib/axios";
import { useAuthStore } from "../store/useAuthStore";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Search, Hotel, Info, MessageCircle, Menu, X, Star, Award, ChevronDown, Settings, User, LogOut, Shield, Newspaper } from "lucide-react";

export default function Navbar() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: authUser, isLoading: authLoading } = useQuery({
    queryKey: ["authUser"],
    queryFn: async () => {
      const res = await axiosInstance.get("/auth/me");
      return res.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  const logoutStore = useAuthStore((state) => state.logout);
  const socket = useAuthStore((state) => state.socket);

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState(location.pathname);
  const profileMenuRef = useRef(null);
  const profileButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const notificationsRef = useRef(null);

  const { data: unread = 0 } = useQuery({
    queryKey: ["unreadCounts"],
    queryFn: () =>
      axiosInstance.get("/messages/unread-counts").then((res) => res.data.count),
    refetchOnWindowFocus: false,
  });

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update active nav item based on location
  useEffect(() => {
    setActiveNavItem(location.pathname);
  }, [location.pathname]);

  // Listen for new messages and update unread count
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = async (msg) => {
      try {
        toast.success(`New message`);
        queryClient.invalidateQueries({ queryKey: ["unreadCounts"] });
      } catch (err) {
        console.error("Failed to fetch sender profile:", err);
      }
    };

    socket.on("newMessage", handleNewMessage);

    return () => {
      socket.off("newMessage", handleNewMessage);
    };
  }, [socket, queryClient, location]);

  // Close profile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(event.target)
      ) {
        setIsProfileMenuOpen(false);
      }

      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest(".mobile-menu-button")
      ) {
        setIsMobileMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  // Logout Function
  const { mutate: logout } = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.post("/auth/logout");
      return response.data;
    },
    onSuccess: async () => {
      await logoutStore();
      queryClient.invalidateQueries({ queryKey: ["authUser"] });
      if (socket) {
        socket.disconnect();
      }
      setIsProfileMenuOpen(false);
      setIsMobileMenuOpen(false);
      navigate('/login');
    },
    onError: (error) => {
      toast.error(error.message || "Logout failed");
    },
  });

  // Profile Menu Component
  const ProfileMenu = () => (
    <div
      ref={profileMenuRef}
      className="absolute right-0 mt-3 w-56 bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 py-2 z-[110] transform transition-all duration-300 ease-out"
      style={{ position: 'absolute', top: '100%', right: 0 }}
    >
      <div className="px-4 py-3 border-b border-gray-100">
        <p className="text-sm font-medium text-gray-900">{authUser?.firstName} {authUser?.lastName}</p>
        <p className="text-xs text-gray-500 capitalize">{authUser?.role}</p>
      </div>
      
      {authUser?.role === "admin" && (
        <Link
          to="/admin-dashboard"
          className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 transition-all duration-200 group"
          onClick={() => setIsProfileMenuOpen(false)}
        >
          <Award className="w-4 h-4 mr-3 text-orange-500 group-hover:scale-110 transition-transform duration-200" />
          Admin Dashboard
        </Link>
      )}
      {authUser?.role === "owner" && (
        <Link
          to="/owner-dashboard"
          state={{ authUser }}
          className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 transition-all duration-200 group"
          onClick={() => setIsProfileMenuOpen(false)}
        >
          <Hotel className="w-4 h-4 mr-3 text-orange-500 group-hover:scale-110 transition-transform duration-200" />
          Owner Dashboard
        </Link>
      )}
      {authUser?.role === "user" && authUser.isVerified && (
        <Link
          to="/account-page"
          state={{ authUser }}
          className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 transition-all duration-200 group"
          onClick={() => setIsProfileMenuOpen(false)}
        >
          <svg className="w-4 h-4 mr-3 text-orange-500 group-hover:scale-110 transition-transform duration-200" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
          Account
        </Link>
      )}
      {authUser.isVerified && (
        <Link
          to="/messages"
          className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 transition-all duration-200 group"
          onClick={() => {
            setIsProfileMenuOpen(false);
            queryClient.setQueryData(["unreadCount"], 0);
          }}
        >
          <div className="flex items-center">
            <MessageCircle className="w-4 h-4 mr-3 text-orange-500 group-hover:scale-110 transition-transform duration-200" />
            <span>Messages</span>
          </div>
          {unread > 0 && (
            <span className="ml-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full px-2.5 py-1 shadow-lg animate-pulse">
              {unread}
            </span>
          )}
        </Link>
      )}
      <div className="border-t border-gray-100 mt-2">
        <button
          onClick={() => logout()}
          className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-all duration-200 group"
        >
          <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </button>
      </div>
    </div>
  );

  // Mobile Menu Component
  const MobileMenu = () => (
    <div
      ref={mobileMenuRef}
      className="fixed inset-0 z-[120] bg-gradient-to-br from-white via-orange-50/80 to-amber-50/80 backdrop-blur-xl flex flex-col pt-20 pb-6 px-6 transform transition-all duration-500 ease-out"
    >
      <button
        onClick={() => setIsMobileMenuOpen(false)}
        className="absolute top-6 right-6 text-gray-700 hover:text-orange-600 z-[125] p-2 rounded-full hover:bg-orange-100 transition-all duration-200"
      >
        <X className="h-6 w-6" />
      </button>

      <div className="flex flex-col space-y-2 mt-8">
        <Link
          to="/partners"
          className="group flex items-center text-lg font-medium text-gray-800 py-4 px-4 rounded-xl hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 hover:shadow-md transition-all duration-300 border-b border-gray-200/50 hover:border-transparent transform hover:scale-[1.02]"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Hotel className="w-5 h-5 mr-3 text-emerald-500 group-hover:scale-110 group-hover:-rotate-12 transition-transform duration-200" />
          <span className="group-hover:text-emerald-700">Be Our Partner</span>
        </Link>

        <Link
          to="/about"
          className="group flex items-center text-lg font-medium text-gray-800 py-4 px-4 rounded-xl hover:bg-gradient-to-r hover:from-orange-50 hover:to-red-50 hover:shadow-md transition-all duration-300 border-b border-gray-200/50 hover:border-transparent transform hover:scale-[1.02]"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Info className="w-5 h-5 mr-3 text-orange-500 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-200" />
          <span className="group-hover:text-orange-700">About Us</span>
        </Link>

        <Link
          to="/blog"
          className="group flex items-center text-lg font-medium text-gray-800 py-4 px-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:shadow-md transition-all duration-300 border-b border-gray-200/50 hover:border-transparent transform hover:scale-[1.02]"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Newspaper className="w-5 h-5 mr-3 text-blue-500 group-hover:scale-110 group-hover:rotate-[-12deg] transition-transform duration-200" />
          <span className="group-hover:text-blue-700">Blog</span>
        </Link>

        {authUser ? (
          <>
            {authUser?.role === "admin" && (
              <Link
                to="/admin-dashboard"
                className="flex items-center text-lg font-medium text-gray-800 py-4 px-4 rounded-xl hover:bg-white/70 hover:shadow-md transition-all duration-300 group border-b border-gray-200/50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Award className="w-5 h-5 mr-3 text-orange-500 group-hover:scale-110 transition-transform duration-200" />
                Admin Dashboard
              </Link>
            )}
            {authUser?.role === "owner" && (
              <Link
                to="/owner-dashboard"
                state={{ authUser }}
                className="flex items-center text-lg font-medium text-gray-800 py-4 px-4 rounded-xl hover:bg-white/70 hover:shadow-md transition-all duration-300 group border-b border-gray-200/50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Hotel className="w-5 h-5 mr-3 text-orange-500 group-hover:scale-110 transition-transform duration-200" />
                Owner Dashboard
              </Link>
            )}
            {authUser?.role === "user" && (
              <Link
                to="/account-page"
                state={{ authUser }}
                className="flex items-center text-lg font-medium text-gray-800 py-4 px-4 rounded-xl hover:bg-white/70 hover:shadow-md transition-all duration-300 group border-b border-gray-200/50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="w-5 h-5 mr-3 text-orange-500 group-hover:scale-110 transition-transform duration-200" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                Account
              </Link>
            )}
            <Link
              to="/messages"
              className="flex items-center justify-between text-lg font-medium text-gray-800 py-4 px-4 rounded-xl hover:bg-white/70 hover:shadow-md transition-all duration-300 group border-b border-gray-200/50"
              onClick={() => {
                setIsMobileMenuOpen(false);
                queryClient.setQueryData(["unreadCount"], 0);
              }}
            >
              <div className="flex items-center">
                <MessageCircle className="w-5 h-5 mr-3 text-orange-500 group-hover:scale-110 transition-transform duration-200" />
                <span>Messages</span>
              </div>
              {unread > 0 && (
                <span className="ml-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full px-2.5 py-1 shadow-lg animate-pulse">
                  {unread}
                </span>
              )}
            </Link>
            <button
              onClick={() => logout()}
              className="flex items-center text-lg font-medium text-red-600 py-4 px-4 rounded-xl hover:bg-red-50 transition-all duration-300 group text-left mt-4"
            >
              <svg className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </>
        ) : (
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 text-lg font-medium text-black bg-white/30 backdrop-blur-sm border border-white/30 rounded-full hover:bg-white/60 hover:border-white/40 transition-all duration-300 shadow-sm hover:shadow-md mt-4"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <svg 
              className="w-5 h-5" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
            <span>Login / Sign Up</span>
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <header className={`bg-gradient-to-r from-[#e98c55] to-[#dfa981] shadow-sm shadow-black transition-all duration-300 relative z-[100] ${
      isScrolled ? 'shadow-lg' : 'shadow-sm shadow-black'
    }`}>
      <nav className="flex items-center justify-between px-6 py-1.5">
        {/* Left Section - Logo and Navigation Links */}
        <div className="flex items-end space-x-8 z-10 pb-1">
          {/* Logo */}
          <div className="flex items-center transition-all duration-300 hover:scale-105 group pl-4">
            <Link to="/">
              <img
                src={logo}
                alt="StayFur Logo"
                className="h-14 w-auto object-contain scale-150 drop-shadow-lg group-hover:drop-shadow-2xl transition-all duration-300"
              />
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-baseline space-x-1">

            <Link
              to="/partners"
              className="flex items-center text-gray-800 text-base font-semibold hover:text-gray-600 px-3 py-1 transition-colors duration-200"
            >
              <Hotel className="w-4 h-4 mr-1" />
              Be Our Partner
            </Link>

            <Link
              to="/about"
              className="flex items-center text-gray-800 text-base font-semibold hover:text-gray-600 px-3 py-1 transition-colors duration-200"
            >
              <Info className="w-4 h-4 mr-1" />
              About Us
            </Link>

            <Link
              to="/blog"
              className="flex items-center text-gray-800 text-base font-semibold hover:text-gray-600 px-3 py-1 transition-colors duration-200"
            >
              <Newspaper className="w-4 h-4 mr-1" />
              Blog
            </Link>
          </div>
        </div>

        <div className="flex-1"></div>

        {/* Right Section - Profile/Login and Mobile Menu */}
        <div className="flex items-center space-x-4 z-10">
          {/* Desktop Profile/Login Section */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Points Feature for Users */}
            {authUser?.role === "user" && (
              <Link
                to="/account-page"
                state={{ authUser, initialTab: "points" }}
                className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-300 hover:from-orange-600 hover:to-amber-500 px-4 py-1 rounded-full shadow-md hover:shadow-lg transition-all duration-300 group"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-white"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z" clipRule="evenodd" />
                  <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
                </svg>
                <span className="text-white font-bold">
                  {Math.floor(authUser.points)}
                </span>
                <span className="text-white text-xs font-medium">POINTS</span>
              </Link>
            )}

            {/* Profile or Login */}
            {authUser ? (
              <div className="relative pr-4">
                <div
                  ref={profileButtonRef}
                  className="relative p-0.5 rounded-full bg-gradient-to-r from-stone-300 to-stone-100 hover:from-stone-400 hover:to-stone-200 transition-all duration-300 cursor-pointer"
                  onClick={toggleProfileMenu}
                >
                  <img
                    src={authUser?.profileImg || profileImg}
                    alt="User Avatar"
                    className="w-9 h-9 rounded-full object-cover"
                  />
                  {unread > 0 && (
                    <span className="absolute top-0 right-0 w-2 h-2 bg-red-600 rounded-full" />
                  )}
                </div>
                {isProfileMenuOpen && <ProfileMenu />}
              </div>
            ) : (
              <div className="mr-4">
                <Link 
                  to="/login" 
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-black font-medium bg-white/30 backdrop-blur-sm border border-white/30 rounded-full hover:bg-white/60 hover:border-white/40 transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-4 w-4" 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">Login / Sign Up</span>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Section */}
          <div className="md:hidden flex items-center space-x-3 pr-4">
            {/* Points Feature for Mobile */}
            {authUser?.role === "user" && (
              <Link
                to="/account-page"
                state={{ authUser, initialTab: "points" }}
                className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-300 px-3 py-1.5 rounded-full shadow-md transition-all duration-300 group"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-white"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z" clipRule="evenodd" />
                  <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
                </svg>
                <span className="text-white font-bold text-sm">
                  {Math.floor(authUser.points)}
                </span>
                <span className="text-white text-xs font-medium">POINTS</span>
              </Link>
            )}

            {/* Profile Image or Login Button */}
            {authUser ? (
              <div className="relative">
                <div className="relative p-0.5 rounded-full bg-gradient-to-r from-stone-300 to-stone-100 hover:from-stone-400 hover:to-stone-200 transition-all duration-300">
                  <img
                    src={authUser.profileImg || profileImg}
                    alt="User Avatar"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="w-9 h-9 rounded-full cursor-pointer object-cover mobile-menu-button"
                  />
                  {unread > 0 && (
                    <span className="absolute top-0 right-0 w-2 h-2 bg-red-600 rounded-full" />
                  )}
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-4 py-2 text-black font-medium bg-white/30 backdrop-blur-sm border border-white/30 rounded-full hover:bg-white/60 hover:border-white/40 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                <svg 
                  className="w-4 h-4" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">Login</span>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Visible only when toggled */}
      {isMobileMenuOpen && <MobileMenu />}
    </header>
  );
}